import { requestDownloadLink } from '@/shared/services/downloadLink.service';
import { get, getBlob, post } from '@/shared/utils/FetchV2.util';
import type {
	ChatConversation,
	ChatConversationSummary,
	ChatMessage,
	ChatMessagesPage,
	SendChatMediaBody,
	SendChatMessageBody,
} from '../types';

const SERVICE = 'communications';
const CHAT = '/app/chat';

/** El engine devuelve `attachments[].url` con su ruta completa; el cliente HTTP añade el prefijo del servicio. */
const MEDIA_PATH = /\/app\/chat\/media\/[^?#]+/;
const MEDIA_ID = /\/app\/chat\/media\/([^/?#]+)/;

/** El id con el que el engine nombra un adjunto, tomado de `attachments[].url`. */
export const chatMediaIdOf = (url: string): string | undefined => {
	const id = MEDIA_ID.exec(url)?.[1];
	return id ? decodeURIComponent(id) : undefined;
};

/** Sin `conversationId` la ruta es la del chat general; con él, la de esa conversación del usuario. */
const withConversation = (path: string, conversationId?: string, query = new URLSearchParams()) => {
	if (conversationId) query.set('conversationId', conversationId);
	const search = query.toString();
	return search ? `${path}?${search}` : path;
};

/**
 * Contrato de heru-communications-engine para el usuario final (token de UME). Cada ruta acepta un
 * `conversationId` opcional: sin él, el chat general de la app; con él, otra conversación del
 * usuario (la de un ticket del CRM).
 */
export const agoraChatClient = {
	/** El chat general solo aparece si ya existe. */
	listConversations: () =>
		get<{ items: ChatConversationSummary[] }>({ service: SERVICE, endpoint: `${CHAT}/conversations` }),

	getConversation: (conversationId?: string) =>
		get<ChatConversation>({ service: SERVICE, endpoint: withConversation(`${CHAT}/conversation`, conversationId) }),

	getMessages: ({ after, limit }: { after?: string; limit: number }, conversationId?: string) => {
		const query = new URLSearchParams({ limit: String(limit) });
		if (after) query.set('after', after);
		return get<ChatMessagesPage>({
			service: SERVICE,
			endpoint: withConversation(`${CHAT}/messages`, conversationId, query),
		});
	},

	/** Si la conversación estaba cerrada, el engine la reabre. */
	sendMessage: (body: SendChatMessageBody, conversationId?: string) =>
		post<ChatMessage>({ service: SERVICE, endpoint: withConversation(`${CHAT}/messages`, conversationId), body }),

	/** Un archivo por envío; el engine es idempotente por `clientMessageId`. Sin `skipAccountError` un 409 no llegaría como error. */
	sendMedia: ({ file, clientMessageId, caption, advisorName }: SendChatMediaBody, conversationId?: string) => {
		const body = new FormData();
		body.append('file', file);
		body.append('clientMessageId', clientMessageId);
		if (caption) body.append('caption', caption);
		if (advisorName) body.append('advisorName', advisorName);
		return post<ChatMessage>({
			service: SERVICE,
			endpoint: withConversation(`${CHAT}/media`, conversationId),
			body,
			skipAccountError: true,
		});
	},

	close: async (conversationId?: string) => {
		await post({ service: SERVICE, endpoint: withConversation(`${CHAT}/close`, conversationId), body: {} });
	},

	markRead: async (conversationId?: string) => {
		await post({ service: SERVICE, endpoint: withConversation(`${CHAT}/read`, conversationId), body: {} });
	},

	/** Baja un adjunto con el token (`<img src>` no lo manda); recibe `attachments[].url` tal cual. */
	fetchMedia: (url: string, conversationId?: string) => {
		const path = MEDIA_PATH.exec(url)?.[0];
		if (!path) return Promise.reject(new Error(`Ruta de adjunto inesperada: ${url}`));
		return getBlob({ service: SERVICE, endpoint: withConversation(path, conversationId) });
	},

	/** Una liga firmada de 5 minutos al adjunto: sin sesión, para que el navegador del sistema lo abra desde la app nativa. */
	createMediaLink: (messageId: string, conversationId?: string) =>
		requestDownloadLink(SERVICE, withConversation(`${CHAT}/media/${encodeURIComponent(messageId)}/link`, conversationId), {}),
};
